/**
 * Rapport de tour — « Ce tour-ci ».
 *
 * Diff avant/après des indicateurs à chaque tour, calculé côté front
 * (on dispose des deux WorldState dans le store au moment du tour).
 * Répond au manque d'attribution causale relevé à l'audit lisibilité
 * (2026-06-10) : un indicateur bougeait sans que le joueur sache de
 * combien ni dans quel contexte.
 *
 * Honnêteté de l'attribution : le moteur ne décompose pas (encore) les
 * variations par cause — le rapport liste donc le **contexte** du tour
 * (décision jouée + événements marquants), pas une imputation exacte.
 * L'attribution fine viendra avec les révélations de mécanisme.
 */

import type { Decision, GameEvent, WorldState } from "./types";
import { deriveAggregates } from "./types";
import type { Verdict } from "./indicators";
import {
  displayEpsilonFor,
  formatDeltaValue,
  INDICATORS,
  VERDICT_LABEL,
} from "./indicators";

export interface IndicatorChange {
  id: string;
  label: string;
  before: number;
  after: number;
  delta: number;
  /** Delta formaté signé (ex. « −3,2 Md€ ») — null jamais ici (filtré). */
  formattedDelta: string;
  direction: "up" | "down";
  /** Multiple du seuil de bruit — sert au tri (plus gros mouvement d'abord). */
  significance: number;
  /** Renseigné quand le mouvement fait changer de palier qualitatif. */
  verdictShift: { from: Verdict; to: Verdict } | null;
}

export interface TurnCause {
  /** Kicker de la décision jouée (ex. « DÉCISION OPÉRATIONNELLE · EMPLOI »). */
  decisionKicker: string | null;
  /** Label de l'option choisie. */
  optionLabel: string | null;
  /** Événements marquants du tour (≥ incident), avec l'impact de
   *  satisfaction réellement appliqué (R4c — attribution causale). */
  events: { text: string; satDelta?: number }[];
}

export interface TurnReport {
  turn: number;
  /** Mouvements significatifs, triés du plus fort au plus faible. */
  changes: IndicatorChange[];
  /** Nombre de mouvements sous le seuil (mentionnés sans être listés). */
  quietCount: number;
  cause: TurnCause;
}

/** Tronque un narratif d'événement pour la ligne de contexte. */
function shorten(text: string, max = 90): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Seuil de significativité propre au RAPPORT — distinct du seuil
 * d'affichage du bandeau. Le PIB croît organiquement de +15..21 Md€
 * chaque tour (R0) : avec le seuil du bandeau (0,1 Md€), il serait le
 * « top mouvement » de chaque tour avec une significance ~160×, noyant
 * les vrais signaux (tuning 2026-06-10). À 25 Md€ — au-dessus de la
 * dérive organique maximale — seuls les chocs anormaux (récession,
 * choc de croissance) remontent au panneau « Ce tour-ci ». Le bandeau,
 * lui, continue d'afficher le delta organique : c'est le contexte du
 * chiffre, pas un signal d'action.
 */
function reportEpsilonFor(def: (typeof INDICATORS)[number]): number {
  if (def.id === "gdp") return 25e9;
  // Le sondage garde un frémissement RNG de ±0,5 pt (moteur) : sous ce
  // seuil, un mouvement d'approbation est du bruit de mesure, pas un
  // signal — il reste visible au bandeau mais pas au rapport.
  if (def.id === "approval") return 0.5;
  // La démographie de confiance (R10) dérive de ~2-5 k hab/mois en
  // continu : c'est l'ambiance, pas un événement. Seuls les mouvements
  // brutaux (> 10 k, ex. impact direct d'une décision) sont rapportés.
  if (def.id === "population") return 10e3;
  return displayEpsilonFor(def);
}

/**
 * Construit le rapport du tour à partir des deux états successifs.
 *
 * `optionId` : option jouée ce tour (null = tour passé sans décision).
 * `decisions` : catalogue complet, pour retrouver la décision par option.
 */
export function buildTurnReport(
  prevState: WorldState,
  nextState: WorldState,
  decisions: Decision[],
  optionId: string | null,
  events: GameEvent[],
  eventImpacts: { narrative: string; sat_delta: number }[] = [],
): TurnReport {
  const prevAgg = deriveAggregates(prevState);
  const nextAgg = deriveAggregates(nextState);

  const changes: IndicatorChange[] = [];
  let quietCount = 0;

  for (const def of INDICATORS) {
    // Même règle de visibilité que le bandeau : un indicateur pas encore
    // révélé n'existe pas pour le joueur, il n'a rien à faire au rapport.
    if (nextState.turn < (def.revealedFromTurn ?? 0)) continue;
    const before = def.extract(prevState, prevAgg);
    const after = def.extract(nextState, nextAgg);
    const delta = after - before;
    const epsilon = reportEpsilonFor(def);
    const formattedDelta = formatDeltaValue(def, delta);
    if (formattedDelta === null || Math.abs(delta) < epsilon) {
      if (delta !== 0) quietCount += 1;
      continue;
    }
    const verdictBefore = def.verdict ? def.verdict(before) : null;
    const verdictAfter = def.verdict ? def.verdict(after) : null;
    changes.push({
      id: def.id,
      label: def.label,
      before,
      after,
      delta,
      formattedDelta,
      direction: delta > 0 ? "up" : "down",
      significance: Math.abs(delta) / epsilon,
      verdictShift:
        verdictBefore && verdictAfter && verdictBefore !== verdictAfter
          ? { from: verdictBefore, to: verdictAfter }
          : null,
    });
  }
  changes.sort((a, b) => b.significance - a.significance);

  // Contexte : décision jouée ce tour, retrouvée par l'id de l'option.
  let decisionKicker: string | null = null;
  let optionLabel: string | null = null;
  if (optionId) {
    for (const d of decisions) {
      const opt = d.options.find((o) => o.id === optionId);
      if (opt) {
        decisionKicker = d.kicker || d.prompt || d.id;
        optionLabel = opt.label;
        break;
      }
    }
  }

  // Événements marquants — les faits divers L1 sont du décor, pas du signal.
  // R4c : chaque événement porte l'impact de satisfaction qu'il a
  // réellement appliqué (rapproché par narrative).
  const impactByNarrative = new Map(
    eventImpacts.map((i) => [i.narrative, i.sat_delta]),
  );
  const notableEvents = events
    .filter((e) => e.urgency !== "fait_divers")
    .map((e) => ({
      text: shorten(e.narrative),
      satDelta: impactByNarrative.get(e.narrative),
    }));

  return {
    turn: nextState.turn,
    changes,
    quietCount,
    cause: { decisionKicker, optionLabel, events: notableEvents },
  };
}

/** Libellé d'une bascule de verdict (ex. « sain → préoccupant »). */
export function verdictShiftLabel(shift: {
  from: Verdict;
  to: Verdict;
}): string {
  return `${VERDICT_LABEL[shift.from]} → ${VERDICT_LABEL[shift.to]}`;
}
